/**
 * `ContentPageEmptyState` — the shared "nothing to show" body handed to
 * `ContentListComponent.emptyState` by the two content `DETAIL_ROUTES` pages
 * (`UserVideosPage`, `ContentDetailsPage`).
 *
 * Both rendered the same glyph + heading; only the copy differed
 * (`noVideosAvailable` vs `contentNotFound`), so the localized string is the
 * single prop — the caller reads it from `useLocale()` and passes it in, keeping
 * the key choice visible at the page that owns the meaning.
 *
 * Flutter's counterpart is icon-less: `content_list_widget.dart:1050-1068`
 * renders a bare "No data" text for the empty list. The glyph is a pre-existing
 * web-only addition, so there is nothing to port here and no Flutter asset to
 * mirror.
 */

import { useTheme } from '@/presentation/contexts/ThemeContext';

export interface ContentPageEmptyStateProps {
    /** Localized heading, e.g. `l10n.content.noVideosAvailable`. */
    title: string;
}

export const ContentPageEmptyState = ({ title }: ContentPageEmptyStateProps) => {
    const { theme } = useTheme();

    return (
        <div className="text-center py-12">
            <div className="mb-4" style={{ color: theme.text.secondary }}>
                {/* Inline SVG kept (not an `AssetIcon`): no video-camera glyph exists under
                    `/assets/images/` — `movie.png` is a play triangle, `camera.png` a photo
                    camera, `live_stream.png` broadcast waves — and inventing an asset for a
                    web-only, Flutter-less decoration is worse than moving this verbatim. */}
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            </div>
            <h3 className="text-lg font-medium mb-2" style={{ color: theme.text.primary }}>
                {title}
            </h3>
        </div>
    );
};
